/**
 * deviceRegistrationStore — remembers the outcome of the last device registration.
 *
 * Why this exists: `/auth/register-device` returns **HTTP 201 even when it
 * refuses**. When the merchant's plan device limit is reached the body is
 * `{ success: true, registered: false, warning: 'Device limit reached (N)...' }`.
 * A client that only checks the status code believes it registered.
 *
 * That matters more on web than on Desktop. The backend allocates a device's
 * slot during `/api/sync/pull` by *updating* the device row, so an unregistered
 * device gets a 503 ("Failed to allocate device slot — aborting sync pull") on
 * every sync. Without this store the operator sees a till that bills fine but
 * never syncs, with no explanation.
 *
 * Billing still works unregistered — sales are written locally — so this is a
 * warning surfaced to the operator, not a hard block.
 */
import { create } from 'zustand';

export interface DeviceRegistrationState {
  /** null until a registration has been attempted. */
  registered: boolean | null;
  /** Server-supplied reason when `registered` is false. */
  warning: string | null;
  /** Set when the request itself failed (offline, 5xx). */
  error: string | null;
  setResult: (r: { registered: boolean; warning?: string }) => void;
  setError: (message: string) => void;
}

export const useDeviceRegistrationStore = create<DeviceRegistrationState>((set) => ({
  registered: null,
  warning: null,
  error: null,
  setResult: ({ registered, warning }) =>
    set({ registered, warning: warning ?? null, error: null }),
  setError: (message) => set({ error: message }),
}));
